<template>
  <div>
    <AnalystQueryForm
      ref="queryForm"
      :loading="submitting"
      @submit="handleSubmit"
    />

    <div v-if="errorMessage" class="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
      <p class="text-sm text-red-700">{{ errorMessage }}</p>
    </div>

    <div v-if="manageJobs.length > 0 || loadingJobs" class="mt-5 pt-4 border-t border-gray-200">
      <h3 class="text-sm font-semibold text-gray-700 mb-2">Recent Queries</h3>
      <AnalystHistory
        :jobs="manageJobs"
        :loading="loadingJobs"
        :active-job-id="activeJob?.id"
        @select="handleSelectJob"
      />
    </div>

    <!-- Superuser: All Analysis View -->
    <div v-if="isSuperUser" class="mt-6 bg-gray-50 rounded-lg border border-gray-200 p-4">
      <div class="flex items-center justify-between mb-3">
        <h3 class="text-sm font-semibold text-gray-500 uppercase tracking-wide">Superuser — All Analysis</h3>
        <button
          type="button"
          class="px-3 py-1.5 text-xs bg-orange-50 hover:bg-orange-100 border border-orange-300 rounded-full text-orange-700 font-medium transition-colors"
          :disabled="submitting"
          @click="submitReport"
        >
          Generate weekly engagement report
        </button>
      </div>
      <AnalystHistory
        :jobs="jobs"
        :loading="loadingJobs"
        :active-job-id="activeJob?.id"
        :show-user="true"
        @select="handleSelectJob"
      />
    </div>

    <AnalystResultModal
      v-if="showResultModal && activeJob"
      :job="activeJob"
      :polling="polling"
      :regenerating="regenerating"
      @follow-up="handleFollowUp"
      @feedback-changed="handleFeedbackChanged"
      @regenerate="handleRegenerate"
      @close="showResultModal = false"
    />
  </div>
</template>

<script>
import AnalystQueryForm from './AnalystQueryForm.vue'
import AnalystHistory from './AnalystHistory.vue'
import AnalystResultModal from './AnalystResultModal.vue'

export default {
  name: 'AnalystPanel',
  components: {
    AnalystQueryForm,
    AnalystHistory,
    AnalystResultModal,
  },
  data() {
    return {
      jobs: [],
      activeJob: null,
      loadingJobs: false,
      submitting: false,
      polling: false,
      pollInterval: null,
      errorMessage: null,
      showResultModal: false,
      regenerating: false,
    }
  },
  computed: {
    isSuperUser() {
      return this.$store.getters['auth/current_user']?.is_superuser || false
    },
    currentUserId() {
      return this.$store.getters['auth/current_user']?.id
    },
    manageJobs() {
      return this.jobs.filter(j => j.user === this.currentUserId || j.job_type === 'weekly_report')
    },
  },
  mounted() {
    this.fetchJobs()
  },
  beforeUnmount() {
    this.stopPolling()
  },
  methods: {
    async fetchJobs() {
      this.loadingJobs = true
      try {
        const response = await this.$axios.get('/api/personal-analyst/jobs/', {
          params: { entity__isnull: true },
        })
        this.jobs = response.data
        // Sync all pending jobs from Snowflake and start polling if any remain pending
        await this.syncPendingJobs()
      } catch (err) {
        this.errorMessage = 'Failed to load query history.'
      } finally {
        this.loadingJobs = false
      }
    },

    async syncPendingJobs() {
      if (!this.jobs.some(j => j.status === 'QUEUED' || j.status === 'PROCESSING')) return

      try {
        const response = await this.$axios.post('/api/personal-analyst/jobs/bulk-sync/', null, {
          params: { entity__isnull: true },
        })
        this.mergeUpdatedJobs(response.data)
      } catch {
        // Sync failure is non-fatal — list still rendered, polling will retry.
      }

      // If any are still pending after sync, start polling
      const stillPending = this.jobs.some(j => j.status === 'QUEUED' || j.status === 'PROCESSING')
      if (stillPending) {
        if (!this.activeJob) this.activeJob = this.jobs.find(j => j.status === 'QUEUED' || j.status === 'PROCESSING')
        this.startPolling()
      }
    },

    mergeUpdatedJobs(updated) {
      for (const job of updated) {
        const idx = this.jobs.findIndex(j => j.id === job.id)
        if (idx !== -1) this.jobs.splice(idx, 1, job)
        if (this.activeJob?.id === job.id) this.activeJob = job
      }
    },

    async selectJob(job) {
      this.errorMessage = null
      this.activeJob = job

      // Immediately fetch latest state via retrieve endpoint (triggers lazy sync)
      try {
        const response = await this.$axios.get(`/api/personal-analyst/jobs/${job.id}/`)
        const updatedJob = response.data
        this.activeJob = updatedJob

        const idx = this.jobs.findIndex(j => j.id === updatedJob.id)
        if (idx !== -1) {
          this.jobs.splice(idx, 1, updatedJob)
        }

        if (updatedJob.status === 'QUEUED' || updatedJob.status === 'PROCESSING') {
          this.startPolling()
        } else {
          this.stopPolling()
        }
      } catch {
        // Fall back to list data if retrieve fails
        if (job.status === 'QUEUED' || job.status === 'PROCESSING') {
          this.startPolling()
        } else {
          this.stopPolling()
        }
      }
    },

    async handleSubmit(formData) {
      this.submitting = true
      this.errorMessage = null
      try {
        const payload = { query: formData.query }
        if (formData.job_type) {
          payload.job_type = formData.job_type
        }
        const response = await this.$axios.post('/api/personal-analyst/jobs/', payload)
        const newJob = response.data
        this.jobs.unshift(newJob)
        this.activeJob = newJob
        this.startPolling()
      } catch (err) {
        if (err.response?.status === 429) {
          this.errorMessage = err.response.data.detail
        } else {
          this.errorMessage = 'Failed to submit query. Please try again.'
        }
      } finally {
        this.submitting = false
      }
    },

    handleSelectJob(job) {
      this.selectJob(job)
      this.showResultModal = true
    },

    submitReport() {
      this.handleSubmit({
        query: 'Generate weekly engagement report',
        job_type: 'weekly_report',
      })
    },

    handleFollowUp(suggestion) {
      this.$refs.queryForm.appendQuery(suggestion)
    },

    handleFeedbackChanged(feedbackItems) {
      if (this.activeJob) {
        this.activeJob = { ...this.activeJob, feedback: feedbackItems }
        const idx = this.jobs.findIndex(j => j.id === this.activeJob.id)
        if (idx !== -1) {
          this.jobs.splice(idx, 1, this.activeJob)
        }
      }
    },

    async handleRegenerate() {
      if (!this.activeJob || this.regenerating) return
      this.regenerating = true
      try {
        const response = await this.$axios.post('/api/personal-analyst/jobs/', {
          job_type: this.activeJob.job_type,
          entity: this.activeJob.entity,
        })
        const newJob = response.data
        this.jobs.unshift(newJob)
        this.activeJob = newJob
        this.startPolling()
      } catch (err) {
        if (err.response?.status === 429) {
          this.errorMessage = err.response.data.detail
        } else {
          this.errorMessage = 'Failed to regenerate. Please try again.'
        }
      } finally {
        this.regenerating = false
      }
    },

    startPolling() {
      this.stopPolling()
      this.polling = true
      this.pollInterval = setInterval(() => this.pollActiveJob(), 30000)
    },

    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval)
        this.pollInterval = null
      }
      this.polling = false
    },

    async pollActiveJob() {
      if (!this.jobs.some(j => j.status === 'QUEUED' || j.status === 'PROCESSING')) {
        this.stopPolling()
        return
      }

      try {
        const response = await this.$axios.post('/api/personal-analyst/jobs/bulk-sync/', null, {
          params: { entity__isnull: true },
        })
        this.mergeUpdatedJobs(response.data)
      } catch {
        // Non-fatal — try again next tick.
      }

      // Stop polling if all done
      if (!this.jobs.some(j => j.status === 'QUEUED' || j.status === 'PROCESSING')) {
        this.stopPolling()
      }
    },
  },
}
</script>
